import filter from 'lodash/filter'
import find from 'lodash/find'
import first from 'lodash/first'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import { useMemo } from 'react'

type PieChartData =
  | {
      bezirk: string
      bevolkerung: string
      nationalitat: string
      _2023: number
    }
  | {
      bezirk: string
      bevolkerung: string
      nationalitat: string
      _2023: string
    }

const FILTER_KEYS = [
  'alle',
  'europäische union (eu)',
  'ausländer',
  'Islamische Länder(OIC) einschl. Syrien'.toLowerCase(),
  'Islamische Länder(OIC)einschl. Syrien'.toLowerCase(),
  'Arabische Länder einschl. Syrien'.toLowerCase(),
  'nicht eindeutig zuordenbar/ ohne Angabe'.toLowerCase(),
]

const filterUnnecessaryData = (data: PieChartData[]) => {
  const filtered_data = orderBy(
    filter(data, (d) => !FILTER_KEYS.includes(d.nationalitat.toLowerCase())),
    ['_2023'],
    'desc'
  )
  return filtered_data
}

export const useDataForPieChart = (region: string, data: PieChartData[]) => {
  const { parsedPopulationData, parsedTop5PopulationData } = useMemo(() => {
    const filteredForRegion = filter(data, (d) => d.bezirk === region)
    const groupedForPopulation = groupBy(filteredForRegion, 'bevolkerung')
    const german_population =
      (first(groupedForPopulation['Deutsche'])?._2023 as number) ?? 0

    const german_population_with_migration =
      (find(
        groupedForPopulation['Deutsche mit Migrationshintergrund'],
        (d) => d.nationalitat.toLowerCase() === 'alle'
      )?._2023 as number) ?? 0

    const other_population =
      (find(
        groupedForPopulation['Ausländer'],
        (d) => d.nationalitat.toLowerCase() === 'ausländer'
      )?._2023 as number) ?? 0

    const other_population_from_eu =
      (find(
        groupedForPopulation['Ausländer'],
        (d) => d.nationalitat.toLowerCase() === 'europäische union (eu)'
      )?._2023 as number) ?? 0

    const parsedPopulationData = [
      {
        // TODO: add translation
        id: 'Deutsche',
        label: 'Deutsche',
        value: german_population - german_population_with_migration,
        color: 'hsl(146, 70%, 50%)',
      },
      {
        id: 'Deutsche mit Migrationshintergrund',
        label: 'Deutsche mit Migrationshintergrund',
        value: german_population_with_migration,
        color: 'hsl(146, 70%, 50%)',
      },
      {
        id: 'Ausländer',
        label: 'Ausländer',
        value: other_population - other_population_from_eu,
        color: 'hsl(172, 70%, 50%)',
      },
      {
        id: 'Ausländer aus EU-Ausland',
        label: 'Ausländer aus EU-Ausland',
        value: other_population_from_eu,
        color: 'hsl(172, 70%, 50%)',
      },
    ]

    const filtered_other_population = filterUnnecessaryData(
      groupedForPopulation['Ausländer']
    )

    const top_5_other_population = filtered_other_population.slice(0, 5)

    const parsedTop5PopulationData = map(
      top_5_other_population,
      (population) => {
        return {
          id: population.nationalitat,
          label: population.nationalitat,
          value: population._2023 as number,
        }
      }
    )

    return { parsedPopulationData, parsedTop5PopulationData }
  }, [data, region])

  return { parsedPopulationData, parsedTop5PopulationData }
}
